import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

interface WritingDraft {
  id: string;
  concept_id: string;
  content: string | null;
  scaffold_generated_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useWritingDraft(
  conceptId: string,
  opts?: { enabled?: boolean },
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["writing-draft", conceptId],
    queryFn: () =>
      apiClient.get(`api/concepts/${conceptId}/writing/draft`).json<WritingDraft>(),
    enabled: !!conceptId && (opts?.enabled ?? true),
    retry: false,
  });

  const saveMutation = useMutation({
    mutationFn: (body: { content: string; scaffold_generated_at?: string | null }) =>
      apiClient
        .put(`api/concepts/${conceptId}/writing/draft`, { json: body })
        .json<WritingDraft>(),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: ["writing-draft", conceptId] });
      const previous = queryClient.getQueryData<WritingDraft>(["writing-draft", conceptId]);
      // Write the optimistic draft even when the cache is empty (the GET may
      // still be in flight — clicking "generate" during that window used to
      // no-op here, leaving the empty state on screen with no feedback while
      // the PUT saved server-side). Synthesize a minimal draft; onSettled's
      // invalidation reconciles with the server row.
      const now = new Date().toISOString();
      queryClient.setQueryData<WritingDraft>(["writing-draft", conceptId], (old) => ({
        id: old?.id ?? "optimistic",
        concept_id: conceptId,
        created_at: old?.created_at ?? now,
        scaffold_generated_at:
          body.scaffold_generated_at ?? old?.scaffold_generated_at ?? null,
        content: body.content,
        updated_at: now,
      }));
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["writing-draft", conceptId], context.previous);
      } else {
        queryClient.removeQueries({ queryKey: ["writing-draft", conceptId] });
      }
    },
    onSettled: () => {
      // Reconcile only when onMutate had to synthesize the row (cache was
      // empty at save time) — the synthetic id must be replaced by the
      // server's. Ordinary autosaves keep the optimistic cache as-is; an
      // unconditional invalidate here would fire a GET per debounced save.
      const cached = queryClient.getQueryData<WritingDraft>(["writing-draft", conceptId]);
      if (cached?.id === "optimistic") {
        queryClient.invalidateQueries({ queryKey: ["writing-draft", conceptId] });
      }
    },
  });

  return { draft: query.data, isLoading: query.isLoading, error: query.error, save: saveMutation };
}
